import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import LoadModal from '../components/LoadModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faChevronLeft,
  faChevronRight,
  faFileExcel,
  faExclamationTriangle,
  faBars,
  faChevronDown,
  faTable,
  faMap,
  faMobileAlt,
  faBell,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import BidsModal from '../components/BidsModal';
import LoadDetailsModal from '../components/LoadDetailsModal';
import DriverInfoModal from '../components/DriverInfoModal';
import DriverReviewModal from '../components/DriverReviewModal';
import LoadIssuesModal from '../components/LoadIssuesModal';
import LoadsMapView from '../components/LoadsMapView';
import { encryptPhoneNumber } from '../utils/phoneEncrypt';
import DriverNotificationModal from '../components/DriverNotificationModal';
import IssueListModal from '../components/IssueListModal';

const Loads = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('pending');
  const [loadIssues, setLoadIssues] = useState({});
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [issueListModalOpen, setIssueListModalOpen] = useState(false);
  const [issues, setIssues] = useState([]);
  const [isBidsModalOpen, setIsBidsModalOpen] = useState(false);
  const [selectedLoadBids, setSelectedLoadBids] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [isDriverInfoModalOpen, setIsDriverInfoModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadsPerPage, setLoadsPerPage] = useState(10);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedDriverForReview, setSelectedDriverForReview] = useState(null);
  const [existingReview, setExistingReview] = useState(null);
  const [isIssuesModalOpen, setIsIssuesModalOpen] = useState(false);
  const [selectedLoadForIssues, setSelectedLoadForIssues] = useState(null);
  const [countries, setCountries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [operationAuthorities, setOperationAuthorities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [senderReceiverCompanies, setSenderReceiverCompanies] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'map'
  const [transferringLoads, setTransferringLoads] = useState(new Set()); // Track which loads are being transferred
  const [sortConfig, setSortConfig] = useState({
    field: 'created_at',
    order: 'desc'
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false, // Enforce 24-hour format
    });
  };

  // Fetch countries from Supabase
  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase
        .from('countries')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name_tr', { ascending: true });

      if (error) throw error;
      setCountries(data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  // Fetch customers from Supabase
  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  // Fetch operation authorities from Supabase
  const fetchOperationAuthorities = async () => {
    try {
      const { data, error } = await supabase
        .from('operation_auths')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setOperationAuthorities(data || []);
    } catch (error) {
      console.error('Error fetching operation authorities:', error);
    }
  };

  // Fetch companies from Supabase
  const fetchCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_type', 'company')
        .order('company_name', { ascending: true });

      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  // Fetch sender/receiver companies from Supabase
  const fetchSenderReceiverCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('sender_receiver_companies')
        .select('*')
        .eq('active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setSenderReceiverCompanies(data || []);
    } catch (error) {
      console.error('Error fetching sender/receiver companies:', error);
    }
  };

  useEffect(() => {
    fetchCountries();
    fetchCustomers();
    fetchOperationAuthorities();
    fetchCompanies();
    fetchSenderReceiverCompanies();
    applyFilters();

    // Check for URL parameters to set active tab
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam === 'agreed') {
      setActiveTab('agreed');
    } else if (tabParam === 'driver') {
      setActiveTab('driver');
    }
  }, [location.search, sortConfig]);

  // Filter states
  const [filters, setFilters] = useState({
    referenceNumber: '',
    loadingCountry: '',
    dischargeCountry: '',
    driver: '',
    towingPlate: '',
    trailerPlate: '',
    customerName: '',
    vehicleType: '',
    operationAuthority: '',
    company: '',
    marketStatus: '',
    senderCompany: '',
    receiverCompany: '',
    loadingDateStart: '',
    loadingDateEnd: '',
    dischargeDateStart: '',
    dischargeDateEnd: '',
  });
  console.log(selectedLoad);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSort = (field) => {
    setSortConfig(prevConfig => ({
      field: field,
      order: prevConfig.field === field && prevConfig.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (field) => {
    if (sortConfig.field !== field) {
      return faSort;
    }
    return sortConfig.order === 'asc' ? faSortUp : faSortDown;
  };

  const showTab = (tabName) => {
    setActiveTab(tabName);
  };

  const handleDriverCancellation = async (loadId) => {
    // Handle canceling the driver for this load
    const { data, error } = await supabase
      .from('loads')
      .update({ accepted_driver: null, status: 'open' })
      .eq('id', loadId)
      .select();
    if (error) throw error;
    console.log('Load updated:', data);
    setIsIssuesModalOpen(false);
    applyFilters();
    // Add your logic here to handle the cancellation
  };

  const fetchBids = async (loadId) => {
    try {
      console.log(loadId);
      const { data, error } = await supabase
        .from('bids')
        .select('*')
        .eq('load_uuid', loadId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching bids:', error);
      return [];
    }
  };

  const assignDriverVehicle = async (loadId) => {
    try {
      const bids = await fetchBids(loadId);
      const load = loads.find((l) => l.uuid === loadId);
      setSelectedLoadBids({ ...load, bids });
      setSelectedLoad(load);
      setIsBidsModalOpen(true);
    } catch (error) {
      console.error('Error opening bids modal:', error);
    }
  };

  const handleAssignDriver = async (driver) => {

    const isConfirmed = window.confirm(
      `${driver.first_name} ${driver.last_name} isimli sürücüyü atamak istediğinize emin misiniz?\n
      Çekici plakası: ${driver.tow_plate}\n
      Dorse plakası: ${driver.trailer_plate}\n`
    );

    if (!isConfirmed) {
      return;
    }

    try {

    const { data: activeLoads, error: activeLoadsError } = await supabase
      .from('loads')
      .select('formatted_id, status')
      .eq('accepted_driver->>id', driver.id)
      .in('status', ['assigned', 'waitingDriver']);

    if (activeLoadsError) {
      throw activeLoadsError;
    }

    if (activeLoads && activeLoads.length > 0) {
      const loadIds = activeLoads.map((l) => l.formatted_id).join(', ');
      alert(
        `Bu sürücü şu an aktif bir yüke atanmış durumda (${loadIds}). Atama işlemi tamamlanamaz.`
      );
      return;
    }

      const { data, error } = await supabase
        .from('loads')
        .update({
          accepted_driver: {
            ...driver,
          },
          status:
            driver?.user_type === 'company_driver'
              ? 'assigned'
              : 'waitingDriver',
        })
        .eq('formatted_id', selectedLoad.formatted_id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Send push notification to the assigned driver
      try {
        const { data: tokens } = await supabase
          .from('profiles')
          .select('push_token')
          .eq('id', driver.id)
          .single();

        if (tokens?.push_token) {
          const message = {
            to: tokens.push_token,
            sound: 'default',
            title: 'Yeni Yük Ataması',
            body: `${selectedLoad.formatted_id} referanslı yük size atandı.`,
            data: { loadId: selectedLoad.id },
          };

          await fetch('https://exp.host/--/api/v2/push/send', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Accept-encoding': 'gzip, deflate',
              'Content-Type': 'application/json',
            },
            mode: 'no-cors',
            body: JSON.stringify(message),
          });
        }
      } catch (notificationError) {
        console.error('Error sending notification:', notificationError);
      }

      // Close modal and refresh data after successful assignment
      setIsBidsModalOpen(false);
      await applyFilters();
    } catch (error) {
      console.error('Error assigning driver:', error);
    }
  };

  const handleAcceptBid = async (bid) => {
    try {
      const { error: loadError } = await supabase
        .from('loads')
        .update({
          status: 'waitingDriver',
          accepted_bid_id: bid.id,
          accepted_driver: bid.bid_type !== 'company' ? bid.driver_info : null,
          company_id: bid.bid_type === 'company' ? bid.driver_info.id : null,
        })
        .eq('id', selectedLoadBids.id);

      if (loadError) throw loadError;

      const { error: bidError } = await supabase
        .from('bids')
        .update({ status: 'accepted' })
        .eq('id', bid.id);

      if (bidError) throw bidError;

      await applyFilters();
      setIsBidsModalOpen(false);
    } catch (error) {
      console.error('Error accepting bid:', error);
    }
  };

  const applyFilters = async () => {
    try {
      setLoading(true);

      // Construct the query
      let query = supabase.from('loads').select('*');

      // Add filters conditionally
      if (filters.referenceNumber) {
        query = query.ilike('formatted_id', `%${filters.referenceNumber}%`);
      }
      if (filters.loadingCountry) {
        query = query.eq('loading_country', filters.loadingCountry);
      }
      if (filters.dischargeCountry) {
        query = query.eq('discharge_country', filters.dischargeCountry);
      }
      if (filters.customerName) {
        query = query.eq('customer_name', filters.customerName);
      }
      if (filters.operationAuthority) {
        // Assuming there's an operation_auth_id field in loads table
        query = query.eq('operation_auth_id', filters.operationAuthority);
      }
      if (filters.company) {
        query = query.eq('company_id', filters.company);
      }
      if (filters.marketStatus) {
        // Map market status to load status or a specific field
        if (filters.marketStatus === 'open') {
          query = query.eq('status', 'open');
        } else if (filters.marketStatus === 'closed') {
          query = query.neq('status', 'open');
        }
      }
      if (filters.senderCompany) {
        query = query.ilike('sender_company', `%${filters.senderCompany}%`);
      }
      if (filters.receiverCompany) {
        query = query.ilike('receiver_company', `%${filters.receiverCompany}%`);
      }
      if (filters.loadingDateStart) {
        query = query.gte('loading_date', filters.loadingDateStart);
      }
      if (filters.loadingDateEnd) {
        query = query.lte('loading_date', filters.loadingDateEnd);
      }
      if (filters.dischargeDateStart) {
        query = query.gte('discharge_date', filters.dischargeDateStart);
      }
      if (filters.dischargeDateEnd) {
        query = query.lte('discharge_date', filters.dischargeDateEnd);
      }

      // Apply sorting based on sortConfig
      query = query.order(sortConfig.field, { ascending: sortConfig.order === 'asc' });

      const { data, error } = await query;

      if (error) throw error;

      const assignedLoadIds = data
      .filter((load) => load.status === 'assigned')
      .map((load) => load.id);

      let issuesData = [];
      const batchSize = 50;
      
      for (let i = 0; i < assignedLoadIds.length; i += batchSize) {
        const batch = assignedLoadIds.slice(i, i + batchSize);
        
        const { data: batchData, error: issuesError } = await supabase
          .from('load_issues')
          .select('*')
          .in('load_id', batch)
          .order('created_at', { ascending: false });

        if (issuesError) throw issuesError;
        
        if (batchData) {
          issuesData = [...issuesData, ...batchData];
        }
      }

      setIssues(issuesData || []);
      
      // Create a map of load_id to issues count
      const issuesMap = {};
      (issuesData || []).forEach((issue) => {
        if (!issuesMap[issue.load_id]) {
          issuesMap[issue.load_id] = 0;
        }
        issuesMap[issue.load_id]++;
      });

      setLoadIssues(issuesMap);
      
      let sortedData = data;
      
      if (filters.driver) {
        sortedData = sortedData.filter((load) => {
          if (!load.accepted_driver) return false;
          const fullName = `${load.accepted_driver.first_name || ''} ${load.accepted_driver.last_name || ''}`.toLowerCase();
          return fullName.includes(filters.driver.toLowerCase());
        });
      }

      if (filters.towingPlate) {
        sortedData = sortedData.filter((load) => {
          if (!load.accepted_driver) return false;
          const towPlates = `${load.accepted_driver.tow_plate || ''}`.toLowerCase();
          return towPlates.includes(filters.towingPlate.toLowerCase());
        });
      }

      if (filters.trailerPlate) {
        sortedData = sortedData.filter((load) => {
          if (!load.accepted_driver) return false;
          const trailerPlates = `${load.accepted_driver.trailer_plate || ''}`.toLowerCase();
          return trailerPlates.includes(filters.trailerPlate.toLowerCase());
        });
      }
      
      setLoads(sortedData);
    } catch (error) {
      console.error('Error applying filters:', error);
      // Handle error (show notification, etc.)
    } finally {
      setLoading(false);
    }
  };

  const openLoadDetails = (load) => {
    setSelectedLoad(load);
    setIsDetailsModalOpen(true);
  };

  const handleEdit = (load) => {
    setSelectedLoad(load ?? null);
    setIsModalOpen(true);
  };

  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [selectedDriverForNotification, setSelectedDriverForNotification] =
    useState(null);

  const openNotificationModal = (driver) => {
    setSelectedDriverForNotification(driver);
    setIsNotificationModalOpen(true);
  };

  // Send login message function
  const sendLoginMessage = async (driver) => {
    try {
      if (!driver.phone_number) {
        alert('Sürücünün telefon numarası bulunamadı');
        return;
      }

      // Encrypt phone number for the mobile redirect link
      const encryptedPhone = encryptPhoneNumber(driver.phone_number.slice(2));
      const mobileRedirectLink = `https://konvoy.digital/mobile-redirect?token=${encryptedPhone}`;

      const messageBody = {
        chatId: driver.phone_number+"@c.us",
        reply_to: null,
        text: `Merhaba ${driver.first_name} ${driver.last_name}, KONVOY uygulamasına giriş yapmak için aşağıdaki linke tıklayın: ${mobileRedirectLink}`,
        linkPreview: true,
        linkPreviewHighQuality: false,
        session: "default"
      }

      const response = await fetch(
        'https://unowp.vericom.com.tr/api/sendText',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Api-Key': 'c28c71dd752c477b8ce85bc959570d83'
          },
          body: JSON.stringify(messageBody),
        }
      );
      console.log(response);

      if (response.ok) {
        alert(
          `Giriş mesajı ${driver.first_name} ${driver.last_name} adlı sürücüye başarıyla gönderildi`
        );
      } else {
        console.log(response);

        throw new Error('Mesaj gönderme başarısız');
      }
    } catch (error) {
      console.error('Error sending login message:', error);
      alert('Mesaj gönderilirken bir hata oluştu');
    }
  };

  const handleSave = async (formData) => {
    try {
      setLoading(true);

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // Handle file uploads
      const fileUploads = await Promise.all(
        formData.files.map(async (file) => {
          // Skip upload for existing files
          if (file.url) return file;

          const filePath = `${user.id}/${Date.now()}-${file.name}`;
          const { data, error: uploadError } = await supabase.storage
            .from('load-documents')
            .upload(filePath, file.file);

          if (uploadError) throw uploadError;

          const {
            data: { publicUrl },
            error: urlError,
          } = supabase.storage.from('load-documents').getPublicUrl(data.path);

          if (urlError) throw urlError;

          return {
            path: data.path,
            name: file.name,
            category: file.category,
            url: publicUrl,
          };
        })
      );

      const loadData = {
        user_id: user.id,
        customer_name: formData.customerName,
        reference_number: formData.referenceNumber,
        sender_company: formData.senderCompany,
        receiver_company: formData.receiverCompany,
        loading_company: formData.loadingCompany,
        loading_city: formData.loadingCity,
        loading_country: formData.loadingCountry,
        pickup_location: formData.uploadAddress,
        loading_date: formData.loading_date,
        discharge_date: formData.dischargeDate,
        unloading_city: formData.evacuationCity,
        discharge_country: formData.dischargeCountry,
        delivery_location: formData.unloadingAddress,
        driver_name: formData.driverName,
        driver_phone: formData.driverPhone,
        towing_plate: formData.towingPlate,
        trailer_plate: formData.trailerPlate,
        cargo_type: formData.cargoType,
        documents: fileUploads,
        created_by: user.id,
        loading_region: formData.loadingRegion,
        load_details: {
          package_type: formData.packageType,
          quantity: formData.quantity,
          product: formData.product,
          net_weight: formData.netWeight,
          gross_weight: formData.grossWeight,
          goods_value: formData.goodsValue,
        },
        goods_currency: formData.goodsCurrency,
        loading_latitude: formData.loadingLatitude,
        loading_longitude: formData.loadingLongitude,
        discharge_latitude: formData.dischargeLatitude,
        discharge_longitude: formData.dischargeLongitude,
        loading_instructions: formData.loadingInstructions,
        discharge_instructions: formData.dischargeInstructions,
        loading_customs_address: formData.loadingCustomsAddress,
        discharge_customs_address: formData.dischargeCustomsAddress,
        vehicle_type: formData.vehicleTypes,
        on_market: formData.on_market,
        transit_time: formData.transitTime,
        operation_auth_id: formData.operation_auth_id,
      };

      let error;
      if (selectedLoad) {
        // Update existing load
        const { error: updateError } = await supabase
          .from('loads')
          .update(loadData)
          .eq('id', selectedLoad.id);
        setIsModalOpen(false);

        error = updateError;
      } else {
        // Create new load
        const { error: insertError } = await supabase.from('loads').insert([
          {
            ...loadData,
            status: 'open',
          },
        ]);
        error = insertError;
      }

      if (error) throw error;

      // Refresh the loads list
      applyFilters();
      setIsModalOpen(false);
      setSelectedLoad(null);
    } catch (error) {
      console.error('Error saving load:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadTransfer = async (load) => {
    // Prevent multiple clicks on the same load
    if (transferringLoads.has(load.id)) {
      return;
    }

    try {
      // Add load to transferring set
      setTransferringLoads(prev => new Set(prev).add(load.id));

      // Create a copy of the load without driver mapping information
      const loadCopy = { ...load };
      
      delete loadCopy.id;
      delete loadCopy.uuid;
      delete loadCopy.formatted_id;
      delete loadCopy.accepted_driver;
      delete loadCopy.accepted_bid_id;
      delete loadCopy.driver_name;
      delete loadCopy.driver_phone;
      delete loadCopy.towing_plate;
      delete loadCopy.trailer_plate;
      delete loadCopy.company_id;
      delete loadCopy.created_at;
      delete loadCopy.updated_at;
      delete loadCopy.loading_tasks;
      delete loadCopy.unloading_tasks;
      delete loadCopy.starting_km;
      delete loadCopy.final_km;
      delete loadCopy.starting_fuel;
      delete loadCopy.final_fuel;

      // Set status to open
      loadCopy.status = 'open';

      // Insert the new load
      const { error } = await supabase.from('loads').insert([loadCopy]);

      if (error) throw error;

      // Refresh the loads list
      await applyFilters();

      // Show success message
      alert(`Yük başarıyla kopyalandı! Yeni yük "${load.formatted_id}" referansından kopyalanarak oluşturuldu.`);
    } catch (error) {
      console.error('Error transferring load:', error);
      alert('Yük kopyalanırken bir hata oluştu: ' + error.message);
    } finally {
      // Remove load from transferring set
      setTransferringLoads(prev => {
        const newSet = new Set(prev);
        newSet.delete(load.id);
        return newSet;
      });
    }
  };

  const handleDeleteLoad = async (loadId) => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      'Bu yükü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve yükle ilgili tüm teklifler, karşı teklifler ve sorunlar da silinecektir.'
    );

    if (!confirmed) {
      return;
    }

    try {
      // First, get all bids for this load
      const { data: bidsData, error: bidsError } = await supabase
        .from('bids')
        .select('id')
        .eq('load_uuid', loadId);

      if (bidsError) throw bidsError;

      // Delete counter offers for all bids related to this load FIRST
      if (bidsData && bidsData.length > 0) {
        const bidIds = bidsData.map((bid) => bid.id);

        // Delete all counter offers first
        const { error: counterOffersError } = await supabase
          .from('counter_offers')
          .delete()
          .in('bid_id', bidIds);

        if (counterOffersError) throw counterOffersError;

        // Wait a moment to ensure deletion is complete
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Now delete all bids for this load
      const { error: bidsDeleteError } = await supabase
        .from('bids')
        .delete()
        .eq('load_uuid', loadId);

      if (bidsDeleteError) throw bidsDeleteError;

      // Delete all load issues for this load
      const { error: issuesError } = await supabase
        .from('load_issues')
        .delete()
        .eq('load_id', loadId);

      if (issuesError) throw issuesError;

      // Delete all driver reviews for this load
      const { error: reviewsError } = await supabase
        .from('driver_reviews')
        .delete()
        .eq('load_id', loadId);

      if (reviewsError) throw reviewsError;

      // Finally, delete the load itself
      const { error: loadError } = await supabase
        .from('loads')
        .delete()
        .eq('id', loadId);

      if (loadError) throw loadError;

      // Refresh the loads list
      await applyFilters();

      // Show success message
      alert('Yük başarıyla silindi.');
    } catch (error) {
      console.error('Error deleting load:', error);
      alert('Yük silinirken bir hata oluştu: ' + error.message);
    }
  };

  const openDriverInfo = (driver) => {
    setSelectedDriver(driver);
    setIsDriverInfoModalOpen(true);
  };

  const openReviewModal = async (load) => {
    setSelectedLoad(load);
    setSelectedDriverForReview(load.accepted_driver);

    // Check if there's an existing review
    try {
      const { data: reviewData } = await supabase
        .from('driver_reviews')
        .select('*')
        .eq('driver_id', load.accepted_driver.id)
        .eq('load_id', load.id)
        .single();

      setExistingReview(reviewData || null);
    } catch (error) {
      console.error('Error fetching existing review:', error);
      setExistingReview(null);
    }

    setIsReviewModalOpen(true);
  };

  const openIssuesModal = (load) => {
    setSelectedLoadForIssues(load);
    setIsIssuesModalOpen(true);
  };

  // Pagination
  const indexOfLastLoad = currentPage * loadsPerPage;
  const indexOfFirstLoad = indexOfLastLoad - loadsPerPage;
  const currentLoads = loads
    .filter((load) => {
      if (activeTab === 'pending') return load.status === 'open';
      if (activeTab === 'driver') return load.status === 'waitingDriver';
      if (activeTab === 'agreed') return load.status === 'assigned';
      if (activeTab === 'completed') return load.status === 'completed';
      return true;
    })
    .slice(indexOfFirstLoad, indexOfLastLoad);

  const totalLoads = loads.filter((load) => {
    if (activeTab === 'pending') return load.status === 'open';
    if (activeTab === 'driver') return load.status === 'waitingDriver';
    if (activeTab === 'agreed') return load.status === 'assigned';
    if (activeTab === 'completed') return load.status === 'completed';
    return true;
  }).length;

  const totalPages = Math.ceil(totalLoads / loadsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  const handleLoadsPerPageChange = (e) => {
    setLoadsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Count functions for tabs
  const getPendingLoadsCount = () => {
    return loads.filter((load) => load.status === 'open').length;
  };

  const getDriverLoadsCount = () => {
    return loads.filter((load) => load.status === 'waitingDriver').length;
  };

  const getAgreedLoadsCount = () => {
    return loads.filter((load) => load.status === 'assigned').length;
  };

  const getCompletedLoadsCount = () => {
    return loads.filter((load) => load.status === 'completed').length;
  };
  const handleReviewSubmit = async ({ rating, comment }) => {
    try {
      if (!selectedDriverForReview || !selectedLoad) {
        console.error('Missing driver or load information');
        return;
      }

      const { error } = await supabase.from('driver_reviews').insert([
        {
          driver_id: selectedDriverForReview.id,
          load_id: selectedLoad.id,
          rating: rating,
          comment: comment,
        },
      ]);

      if (error) throw error;

      // Fetch the review we just created to update the UI
      const { data: reviewData } = await supabase
        .from('driver_reviews')
        .select('*')
        .eq('driver_id', selectedDriverForReview.id)
        .eq('load_id', selectedLoad.id)
        .single();

      if (reviewData) {
        setExistingReview(reviewData);
      }

      setIsReviewModalOpen(false);
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between pb-6">
            <div>
              <h1 className="text-2xl font-medium text-gray-800">Yükler</h1>
              <p className="text-sm text-gray-500">
                Toplam Yük: {loads.length}
              </p>
            </div>
            <div className="flex gap-4">
              <div className="flex border rounded-md overflow-hidden">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-4 py-2 flex items-center ${
                    viewMode === 'table'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <FontAwesomeIcon icon={faTable} className="mr-2" />
                  Tablo
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`px-4 py-2 flex items-center ${
                    viewMode === 'map'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <FontAwesomeIcon icon={faMap} className="mr-2" />
                  Harita
                </button>
              </div>
              <button
                onClick={() => {
                  setIssueListModalOpen(true);
                }}
                className={`${
                  issues.length === 0 
                    ? 'bg-green-500 hover:bg-green-600' 
                    : 'bg-[#eb4242] hover:bg-[#d93b3b]'
                } text-white px-4 py-2 rounded-md flex items-center`}
              >
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                Yük Sorunları ({issues.length})
              </button>
              <button
                onClick={() => {
                  setIsModalOpen(true);
                  setSelectedLoad(null);
                }}
                className="bg-[#4263EB] text-white px-4 py-2 rounded-md hover:bg-[#3b5bd9] flex items-center"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Yeni Yük Ekle
              </button>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Yük Referans Numarası
                </label>
                <input
                  type="text"
                  name="referenceNumber"
                  value={filters.referenceNumber}
                  onChange={handleFilterChange}
                  placeholder="Yük Referans Numarası"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Müşteri
                </label>
                <select
                  name="customerName"
                  value={filters.customerName}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tümü</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.name}>
                      {customer.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Araç Tipi
                </label>
                <select
                  name="vehicleType"
                  value={filters.vehicleType}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tümü</option>
                  <option value="closed_box">Closed Box</option>
                  <option value="standart_curtain">Standard Curtain</option>
                  <option value="refrigerated">Refrigerated</option>
                  <option value="adr">ADR</option>
                  <option value="mega_curtain">Mega Curtain</option>
                  <option value="truck&trailer">Truck & Trailer</option>
                  <option value="minivan">Minivan</option>
                  <option value="lorry">Lorry</option>
                  <option value="out_of_gauge">Out of Gauge (OOG)</option>
                  <option value="flatbed">Flatbed</option>
                  <option value="lowbed">Lowbed</option>
                  <option value="jumbo Trailer">Jumbo Trailer</option>
                  <option value="tanker">Tanker</option>
                  <option value="tilt_trailer">Tilt Trailer</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Yükleme Ülkesi
                </label>
                <select
                  name="loadingCountry"
                  value={filters.loadingCountry}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tümü</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name_tr || country.name_en}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Boşaltma Ülkesi
                </label>
                <select
                  name="dischargeCountry"
                  value={filters.dischargeCountry}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tümü</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name_tr || country.name_en}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sürücü
                </label>
                <input
                  type="text"
                  name="driver"
                  value={filters.driver}
                  onChange={handleFilterChange}
                  placeholder="Sürücü Adı"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Çekici Plakası
                </label>
                <input
                  type="text"
                  name="towingPlate"
                  value={filters.towingPlate}
                  onChange={handleFilterChange}
                  placeholder="Çekici Plakası"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dorse Plakası
                </label>
                <input
                  type="text"
                  name="trailerPlate"
                  value={filters.trailerPlate}
                  onChange={handleFilterChange}
                  placeholder="Dorse Plakası"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Second row of filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Operasyon Yetkilisi
                </label>
                <select
                  name="operationAuthority"
                  value={filters.operationAuthority}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tümü</option>
                  {operationAuthorities.map((auth) => (
                    <option key={auth.id} value={auth.id}>
                      {auth.name} {auth.surname}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Firma
                </label>
                <select
                  name="company"
                  value={filters.company}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tümü</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.company_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pazar Durumu
                </label>
                <select
                  name="marketStatus"
                  value={filters.marketStatus}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tümü</option>
                  <option value="open">Açık</option>
                  <option value="closed">Kapalı</option>
                </select>
              </div>
            </div>

            {/* Third row of filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gönderici Firma
                </label>
                <select
                  name="senderCompany"
                  value={filters.senderCompany}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tümü</option>
                  {senderReceiverCompanies
                    .filter(
                      (company) =>
                        company.company_type === 'sender' ||
                        company.company_type === 'both'
                    )
                    .map((company) => (
                      <option key={company.id} value={company.name}>
                        {company.name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alıcı Firma
                </label>
                <select
                  name="receiverCompany"
                  value={filters.receiverCompany}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tümü</option>
                  {senderReceiverCompanies
                    .filter(
                      (company) =>
                        company.company_type === 'receiver' ||
                        company.company_type === 'both'
                    )
                    .map((company) => (
                      <option key={company.id} value={company.name}>
                        {company.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Yükleme Tarihi Aralığı Başlangıcı
                  </label>
                  <input
                    type="date"
                    name="loadingDateStart"
                    placeholder="Başlangıç"
                    value={filters.loadingDateStart}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="self-center">-</p>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Yükleme Tarihi Aralığı Bitişi
                  </label>
                  <input
                    type="date"
                    name="loadingDateEnd"
                    placeholder="Bitiş"
                    value={filters.loadingDateEnd}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

{/*               
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Boşaltma Tarihi Aralığı Başlangıcı
                  </label>
                  <input
                    type="date"
                    name="dischargeDateStart"
                    placeholder="Başlangıç"
                    value={filters.dischargeDateStart}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="self-center">-</p>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Boşaltma Tarihi Aralığı Bitişi
                  </label>
                  <input
                    type="date"
                    name="dischargeDateEnd"
                    placeholder="Bitiş"
                    value={filters.dischargeDateEnd}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
 */}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={applyFilters}
                disabled={loading}
                className={`
                  px-4 py-2 rounded-md text-white
                  bg-blue-600 hover:bg-blue-700
                  disabled:bg-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-400
                `}
              >
                {loading ? 'Yükleniyor...' : 'Filtreleri Uygula'}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-t-lg shadow">
            <div className="flex border-b">
              <button
                onClick={() => showTab('pending')}
                className={`flex-1 px-4 py-4 text-center border-b-2 ${
                  activeTab === 'pending'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Atama Bekleyen
                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                  {getPendingLoadsCount()}
                </span>
              </button>
              <button
                onClick={() => showTab('driver')}
                className={`flex-1 px-4 py-4 text-center border-b-2 ${
                  activeTab === 'driver'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Sürücü Cevabı Bekleyen
                <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-800 text-xs rounded-full">
                  {getDriverLoadsCount()}
                </span>
              </button>
              <button
                onClick={() => showTab('agreed')}
                className={`flex-1 px-4 py-4 text-center border-b-2 ${
                  activeTab === 'agreed'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Onaylanan
                <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-800 text-xs rounded-full">
                  {getAgreedLoadsCount()}
                </span>
              </button>
              <button
                onClick={() => showTab('completed')}
                className={`flex-1 px-4 py-4 text-center border-b-2 ${
                  activeTab === 'completed'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Tamamlanan
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                  {getCompletedLoadsCount()}
                </span>
              </button>
            </div>
          </div>

          {/* Map View */}
          {viewMode === 'map' && (
            <LoadsMapView
              loads={loads}
              loadIssues={loadIssues}
              onLoadSelect={setSelectedLoad}
              activeTab={activeTab}
              onOpenDetails={openLoadDetails}
            />
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <>
              <div className="bg-white shadow rounded-b-lg overflow-hidden">
                {/* Pending Agreement List */}
                <div className={activeTab === 'pending' ? '' : 'hidden'}>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('created_at')}
                          >
                            <div className="flex items-center gap-2">
                              Referans No
                              <FontAwesomeIcon 
                                icon={getSortIcon('created_at')} 
                                className="text-gray-400"
                              />
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Yükleme
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Boşaltma
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Müşteri
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            İşlemler
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {currentLoads.map((load) => (
                          <tr key={load.id}>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-blue-600">
                                {load.formatted_id}
                              </div>
                              {load.reference_number && (
                                <div className="text-xs text-gray-500">
                                  Ref: {load.reference_number}
                                </div>
                              )}
                              {load.created_at && (
                                <div className="text-xs text-gray-500">
                                  Tarih: {formatDate(load.created_at)}
                                </div>
                              )}
                              
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {load.loading_country}
                              </div>
                              <div className="text-xs text-gray-500">
                                {load.pickup_location}
                              </div>
                              <div className="text-xs text-gray-500">
                                Yükleme Tarihi: {formatDate(load.loading_date)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {load.discharge_country}
                              </div>
                              <div className="text-xs text-gray-500">
                                {load.delivery_location}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {load.customer_name}
                              </div>
                              {(load.sender_company ||
                                load.receiver_company) && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {load.sender_company && (
                                    <div>Gönderici: {load.sender_company}</div>
                                  )}
                                  {load.receiver_company && (
                                    <div>Alıcı: {load.receiver_company}</div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => assignDriverVehicle(load.uuid)}
                                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                                >
                                  Sürücü & Araç Atama
                                </button>
                                <button
                                  onClick={() => openLoadDetails(load)}
                                  className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                                >
                                  Detaylar
                                </button>
                                <button
                                  onClick={() => handleEdit(load)}
                                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                                >
                                  Düzenle
                                </button>
                                <button
                                  onClick={() => handleLoadTransfer(load)}
                                  disabled={transferringLoads.has(load.id)}
                                  className={`px-3 py-1 text-white text-sm rounded ${
                                    transferringLoads.has(load.id)
                                      ? 'bg-purple-400 cursor-not-allowed'
                                      : 'bg-purple-600 hover:bg-purple-700'
                                  }`}
                                >
                                  {transferringLoads.has(load.id) ? 'Kopyalanıyor...' : 'Kopyala'}
                                </button>
                                <button
                                  onClick={() => handleDeleteLoad(load.id)}
                                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                                >
                                  Sil
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className={activeTab === 'agreed' ? '' : 'hidden'}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('created_at')}
                        >
                          <div className="flex items-center gap-2">
                            Referans No
                            <FontAwesomeIcon 
                              icon={getSortIcon('created_at')} 
                              className="text-gray-400"
                            />
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Yükleme
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Boşaltma
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Müşteri
                        </th>

                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Sürücü
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          İlerleme
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {currentLoads.map((load) => (
                        <tr key={load.id}>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-blue-600">
                              {load.formatted_id}
                            </div>
                            {load.reference_number && (
                              <div className="text-xs text-gray-500">
                                Ref: {load.reference_number}
                              </div>
                            )}
                            {load.created_at && (
                              <div className="text-xs text-gray-500">
                                Tarih: {formatDate(load.created_at)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {load.loading_country}
                            </div>
                            <div className="text-xs text-gray-500">
                              {load.pickup_location}
                            </div>
                            <div className="text-xs text-gray-500">
                              Yükleme Tarihi: {formatDate(load.loading_date)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {load.discharge_country}
                            </div>
                            <div className="text-xs text-gray-500">
                              {load.delivery_location}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {load.customer_name}
                            </div>
                            {(load.sender_company || load.receiver_company) && (
                              <div className="text-xs text-gray-500 mt-1">
                                {load.sender_company && (
                                  <div>Gönderici: {load.sender_company}</div>
                                )}
                                {load.receiver_company && (
                                  <div>Alıcı: {load.receiver_company}</div>
                                )}
                              </div>
                            )}
                          </td>

                          <td className="px-6 py-4 text-center align-middle">
                            <div className="flex flex-col items-center">
                              <div className="text-sm text-gray-900">
                                {load.accepted_driver?.first_name}{' '}
                                {load.accepted_driver?.last_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {load.accepted_driver?.trailer_plate}
                              </div>
                              <div className="text-xs text-gray-500">
                                {load.accepted_driver?.tow_plate}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center text-xs text-gray-500">
                                <span className="mr-2">Yükleme:</span>
                                <div className="flex items-center">
                                  <span className="text-green-600 font-medium">
                                    {load.loading_tasks?.filter(
                                      (task) => task.status === 'completed'
                                    ).length || 0}
                                    /{load.loading_tasks?.length || 0}
                                  </span>
                                  <div className="w-20 h-1.5 bg-gray-200 rounded-full ml-2">
                                    <div
                                      className="h-1.5 bg-green-500 rounded-full"
                                      style={{
                                        width: `${
                                          (load.loading_tasks?.filter(
                                            (task) =>
                                              task.status === 'completed'
                                          ).length /
                                            load.loading_tasks?.length) *
                                            100 || 0
                                        }%`,
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center text-xs text-gray-500">
                                <span className="mr-2">Boşaltma:</span>
                                <div className="flex items-center">
                                  <span className="text-gray-600 font-medium">
                                    {
                                      load.unloading_tasks?.filter(
                                        (task) => task.status === 'completed'
                                      ).length
                                    }
                                    /{load.unloading_tasks?.length}
                                  </span>
                                  <div className="w-20 h-1.5 bg-gray-200 rounded-full ml-2">
                                    <div
                                      className="h-1.5 bg-green-500 rounded-full"
                                      style={{
                                        width: `${
                                          (load.unloading_tasks?.filter(
                                            (task) =>
                                              task.status === 'completed'
                                          ).length /
                                            load.unloading_tasks?.length) *
                                            100 || 0
                                        }%`,
                                      }}
                                    ></div>
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openLoadDetails(load);
                                  }}
                                  className="ml-2 text-blue-600 hover:text-blue-800"
                                >
                                  Detaylar
                                </button>
                              </div>
                            </div>
                          </td>
                          <td className=" py-4">
                            <div className="flex">
                              {load.status === 'completed' && (
                                <button
                                  onClick={() => openReviewModal(load)}
                                  className={`w-24 py-1 text-sm rounded ${
                                    existingReview &&
                                    existingReview.load_id === load.id
                                      ? 'bg-green-600 hover:bg-green-700'
                                      : 'bg-yellow-600 hover:bg-yellow-700'
                                  } text-white`}
                                >
                                  {existingReview &&
                                  existingReview.load_id === load.id
                                    ? 'Değerlendirmeyi Gör'
                                    : 'Değerlendir'}
                                </button>
                              )}
                              <div>
                                <button
                                  onClick={() => openLoadDetails(load)}
                                  className="w-24 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                                >
                                  Detaylar
                                </button>

                                <button
                                  onClick={() => handleEdit(load)}
                                  className="w-24 py-1 my-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                                >
                                  Düzenle
                                </button>

                                <button
                                  onClick={() => handleLoadTransfer(load)}
                                  disabled={transferringLoads.has(load.id)}
                                  className={`w-24 py-1 text-white text-sm rounded ${
                                    transferringLoads.has(load.id)
                                      ? 'bg-purple-400 cursor-not-allowed'
                                      : 'bg-purple-600 hover:bg-purple-700'
                                  }`}
                                >
                                  {transferringLoads.has(load.id) ? 'Kopyalanıyor...' : 'Kopyala'}
                                </button>
                              </div>
                              <div className="gap-2">
                                <button
                                  onClick={() => openNotificationModal(load.accepted_driver)}
                                  className="w-36 mb-2 text-left text-blue-600 hover:text-blue-900"
                                >
                                  <FontAwesomeIcon icon={faBell} className="mr-1" />
                                  Bildirim Gönder
                                </button>

                                <button
                                  onClick={() => sendLoginMessage(load.accepted_driver)}
                                  className="w-36 text-left text-green-600 hover:text-green-900"
                                >
                                  <FontAwesomeIcon icon={faMobileAlt} className="mr-1" />
                                  Giriş Mesajı Gönder
                                </button>
                              </div>
                              {loadIssues[load.id] > 0 && (
                                <button
                                  onClick={() => openIssuesModal(load)}
                                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center"
                                >
                                  <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    className="mr-1"
                                  />
                                  Sorun ({loadIssues[load.id]})
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Waiting Driver List */}
              <div className={activeTab === 'driver' ? '' : 'hidden'}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('created_at')}
                        >
                          <div className="flex items-center gap-2">
                            Referans No
                            <FontAwesomeIcon 
                              icon={getSortIcon('created_at')} 
                              className="text-gray-400"
                            />
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Yükleme
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Boşaltma
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Müşteri
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Araçlar
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {currentLoads.map((load) => (
                        <tr key={load.id}>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-blue-600">
                              {load.formatted_id}
                            </div>
                            {load.reference_number && (
                              <div className="text-xs text-gray-500">
                                Ref: {load.reference_number}
                              </div>
                            )}
                            {load.created_at && (
                              <div className="text-xs text-gray-500">
                                Tarih: {formatDate(load.created_at)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {load.loading_country}
                            </div>
                            <div className="text-xs text-gray-500">
                              {load.pickup_location}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {load.discharge_country}
                            </div>
                            <div className="text-xs text-gray-500">
                              {load.delivery_location}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {load.customer_name}
                            </div>
                            {(load.sender_company || load.receiver_company) && (
                              <div className="text-xs text-gray-500 mt-1">
                                {load.sender_company && (
                                  <div>Gönderici: {load.sender_company}</div>
                                )}
                                {load.receiver_company && (
                                  <div>Alıcı: {load.receiver_company}</div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {load.accepted_driver?.tow_plate}
                            </div>
                            <div className="text-xs text-gray-500">
                              {load.accepted_driver?.trailer_plate}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => openDriverInfo(load)}
                                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                              >
                                Sürücü Bilgisi
                              </button>
                              <button
                                onClick={() => openLoadDetails(load)}
                                className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                              >
                                Detaylar
                              </button>
                              <button
                                onClick={() => handleEdit(load)}
                                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                              >
                                Düzenle
                              </button>
                              <button
                                onClick={() => handleLoadTransfer(load)}
                                disabled={transferringLoads.has(load.id)}
                                className={`px-3 py-1 text-white text-sm rounded ${
                                  transferringLoads.has(load.id)
                                    ? 'bg-purple-400 cursor-not-allowed'
                                    : 'bg-purple-600 hover:bg-purple-700'
                                }`}
                              >
                                {transferringLoads.has(load.id) ? 'Kopyalanıyor...' : 'Kopyala'}
                              </button>
                              <button
                                onClick={() =>
                                  handleDriverCancellation(load.id)
                                }
                                className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700"
                              >
                                Sürücü İptal
                              </button>
                              {loadIssues[load.id] > 0 && (
                                <button
                                  onClick={() => openIssuesModal(load)}
                                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center"
                                >
                                  <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    className="mr-1"
                                  />
                                  Sorun ({loadIssues[load.id]})
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Completed List */}
              <div className={activeTab === 'completed' ? '' : 'hidden'}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('created_at')}
                        >
                          <div className="flex items-center gap-2">
                            Referans No
                            <FontAwesomeIcon 
                              icon={getSortIcon('created_at')} 
                              className="text-gray-400"
                            />
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Yükleme
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Boşaltma
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Müşteri
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Sürücü
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {currentLoads.map((load) => (
                        <tr key={load.id}>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-blue-600">
                              {load.formatted_id}
                            </div>
                            {load.reference_number && (
                                <div className="text-xs text-gray-500">
                                  Ref: {load.reference_number}
                                </div>
                            )}
                            {load.created_at && (
                              <div className="text-xs text-gray-500">
                                Tarih: {formatDate(load.created_at)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {load.loading_country}
                            </div>
                            <div className="text-xs text-gray-500">
                              {load.pickup_location}
                            </div>
                            <div className="text-xs text-gray-500">
                              Yükleme Tarihi: {formatDate(load.loading_tasks?.[2]?.completed_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {load.discharge_country}
                            </div>
                            <div className="text-xs text-gray-500">
                              {load.delivery_location}
                            </div>
                            <div className="text-xs text-gray-500">
                              Boşaltma Tarihi: {formatDate(load.unloading_tasks?.[4]?.completed_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {load.customer_name}
                            </div>
                            {(load.sender_company || load.receiver_company) && (
                              <div className="text-xs text-gray-500 mt-1">
                                {load.sender_company && (
                                  <div>Gönderici: {load.sender_company}</div>
                                )}
                                {load.receiver_company && (
                                  <div>Alıcı: {load.receiver_company}</div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center align-middle">
                            <div className="flex flex-col items-center">
                              <div className="text-sm text-gray-900">
                                {load.accepted_driver?.first_name}{' '}
                                {load.accepted_driver?.last_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {load.accepted_driver?.trailer_plate}
                              </div>
                              <div className="text-xs text-gray-500">
                                {load.accepted_driver?.tow_plate}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => openLoadDetails(load)}
                                className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                              >
                                Detaylar
                              </button>
                              <button
                                onClick={() => openReviewModal(load)}
                                className={`px-3 py-1 text-sm rounded ${
                                  existingReview &&
                                  existingReview.load_id === load.id
                                    ? 'bg-green-600 hover:bg-green-700'
                                    : 'bg-yellow-600 hover:bg-yellow-700'
                                } text-white`}
                              >
                                {existingReview &&
                                existingReview.load_id === load.id
                                  ? 'Değerlendirmeyi Gör'
                                  : 'Değerlendir'}
                              </button>
                              <button
                                onClick={() => handleLoadTransfer(load)}
                                disabled={transferringLoads.has(load.id)}
                                className={`px-3 py-1 text-white text-sm rounded ${
                                  transferringLoads.has(load.id)
                                    ? 'bg-purple-400 cursor-not-allowed'
                                    : 'bg-purple-600 hover:bg-purple-700'
                                }`}
                              >
                                {transferringLoads.has(load.id) ? 'Kopyalanıyor...' : 'Kopyala'}
                              </button>

                              {loadIssues[load.id] > 0 && (
                                <button
                                  onClick={() => openIssuesModal(load)}
                                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center"
                                >
                                  <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    className="mr-1"
                                  />
                                  Sorun ({loadIssues[load.id]})
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination - Only show for table view */}
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex flex-col space-y-3 sm:hidden">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Önceki
                    </button>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-700 mr-2">
                        Sayfa başına:
                      </span>
                      <select
                        value={loadsPerPage}
                        onChange={handleLoadsPerPageChange}
                        className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                    </div>
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Sonraki
                    </button>
                  </div>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div className="flex items-center">
                    <p className="text-sm text-gray-700 mr-4">
                      Görüntülenen{' '}
                      <span className="font-medium">
                        {indexOfFirstLoad + 1}
                      </span>{' '}
                      -{' '}
                      <span className="font-medium">
                        {Math.min(indexOfLastLoad, totalLoads)}
                      </span>{' '}
                      of <span className="font-medium">{totalLoads}</span> sonuç
                    </p>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-700 mr-2">
                        Sayfa başına:
                      </span>
                      <select
                        value={loadsPerPage}
                        onChange={handleLoadsPerPageChange}
                        className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <nav
                      className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                      aria-label="Pagination"
                    >
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                          currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          className="h-4 w-4"
                        />
                      </button>

                      {/* Page Numbers */}
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= currentPage - 1 &&
                            pageNumber <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNumber}
                              onClick={() => paginate(pageNumber)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === pageNumber
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        }
                        if (
                          pageNumber === currentPage - 2 ||
                          pageNumber === currentPage + 2
                        ) {
                          return (
                            <span
                              key={pageNumber}
                              className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-gray-700"
                            >
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}

                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                          currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="h-4 w-4"
                        />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Modals */}
      {isModalOpen && (
        <LoadModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedLoad(null);
          }}
          onSave={handleSave}
          load={selectedLoad}
          countries={countries}
        />
      )}

      {isBidsModalOpen && (
        <BidsModal
          isOpen={isBidsModalOpen}
          onClose={() => setIsBidsModalOpen(false)}
          loadBids={selectedLoadBids}
          bids={selectedLoadBids?.bids}
          onAcceptBid={handleAcceptBid}
          loadId={selectedLoad?.formatted_id}
          handleAssignDriver={handleAssignDriver}
        />
      )}

      {isDetailsModalOpen && (
        <LoadDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          load={selectedLoad}
          onOpenDriverInfo={openDriverInfo}
          onOpenReviewModal={openReviewModal}
        />
      )}

      {isDriverInfoModalOpen && (
        <DriverInfoModal
          isOpen={isDriverInfoModalOpen}
          onClose={() => setIsDriverInfoModalOpen(false)}
          driver={selectedDriver}
        />
      )}

      {isReviewModalOpen && (
        <DriverReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          driver={selectedDriverForReview}
          existingReview={existingReview}
          onSubmit={handleReviewSubmit}
        />
      )}

      {issueListModalOpen && (
        <IssueListModal
          isOpen={issueListModalOpen}
          onClose={() => setIssueListModalOpen(false)}
          issues={issues}
          onEditClick={handleEdit}
          onLoadDetailClick={openLoadDetails}
          onCancelDriverClick={handleDriverCancellation}
        />
      )}

      {isIssuesModalOpen && (
        <LoadIssuesModal
          isOpen={isIssuesModalOpen}
          onClose={() => setIsIssuesModalOpen(false)}
          load={selectedLoadForIssues}
          onDetailsClick={openLoadDetails}
          onEditClick={handleEdit}
          onCancelDriverClick={handleDriverCancellation}
        />
      )}

      {isNotificationModalOpen && (
        <DriverNotificationModal
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
        driver={selectedDriverForNotification}
        />
      )}
    </div>
  );
};

export default Loads;